// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// Contracts
import { ProviderItemDto } from '@contracts/api/dto/providers/ProviderDto';
import { Filterable } from '@contracts/common/traits/Filterable';
// Components
import {
  EnumTableColumnHeader,
  StringTableColumnHeader,
  TableColumnHeader,
} from '@Components/shared/tables/columns';
import {
  ProviderListColumns,
  ProviderListFilter,
  providerListColumns,
} from '@utils/tables/providers/providerListFilter';
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import TruncateText from '@Components/truncate-text/TruncateText';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { useProvidersTable } from '@Modal/hooks/providers/useProvidersTable';
import { handleCopyClick } from '@Components/shared/copyText/CoptyText';
import { useSnackbar } from 'notistack';

type HeaderOptions = {
  filter: Filterable<ProviderListFilter>;
};

type Columns = TableColumnDescriptionArray<
  ProviderItemDto,
  HeaderOptions,
  typeof providerListColumns
>;

type Column = TableColumnDescription<ProviderItemDto, HeaderOptions, ProviderListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useProviderTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { handleFilterSelect } = useProvidersTable();
  const { enqueueSnackbar } = useSnackbar();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'provider.id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '320px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'id'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box
              sx={{
                ...ellipsis,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {row?.id}{' '}
              <ContentCopyIcon
                onClick={(event) => handleCopyClick(event, row?.id || '', enqueueSnackbar)}
              />
            </Box>
          ),
        },
        {
          name: 'provider.updatedAt',
          title: 'Updated At',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'provider.name',
          title: 'Name',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'name'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.name}</Box>,
        },
        {
          name: 'provider.apiUrl',
          title: 'Api Url',
          sx: { minWidth: '190px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {
                <>
                  {row?.apiUrl && (
                    <TruncateText text={row?.apiUrl} maxLength={15} textStyles={styles.id} />
                  )}
                </>
              }
            </Box>
          ),
        },
        {
          name: 'provider.syncRoundEnd',
          title: 'Sync Round End',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName="syncRoundEnd"
              options={[
                { key: 'True', value: 'true' },
                { key: 'False', value: 'false' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.syncRoundEnd ? 'True' : 'False'}</Box>,
        },
        {
          name: 'provider.status',
          title: 'Status',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName="status"
              options={[
                { key: 'Active', value: 'ACTIVE' },
                { key: 'Inactive', value: 'INACTIVE' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.status}</Box>,
        },
        {
          name: 'provider.retryScheduledSendCredit',
          title: 'Retry scheduled send credit',
          sx: { minWidth: '180px', maxWidth: '200px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName="retryScheduledSendCredit"
              options={[
                { key: 'True', value: 'true' },
                { key: 'False', value: 'false' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.retryScheduledSendCredit ? 'True' : 'False'}</Box>
          ),
        },
        {
          name: 'provider.refundOnCreditFail',
          title: 'Refund on credit fail',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName="refundOnCreditFail"
              options={[
                { key: 'True', value: 'true' },
                { key: 'False', value: 'false' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.refundOnCreditFail ? 'True' : 'False'}</Box>
          ),
        },
        {
          name: 'provider.filterExternalProviderBets',
          title: 'Filter Ext Casino Bets',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.filterExternalProviderBets ? 'True' : 'False'}</Box>
          ),
        },
        {
          name: 'provider.isInternal',
          title: 'Is Internal',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.isInternal ? 'True' : 'False'}</Box>,
        },
        {
          name: 'provider.rgsId',
          title: 'RGS',
          sx: { minWidth: '80px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.rgsId}</Box>,
        },
        {
          name: 'provider.integrationType',
          title: 'Integration Type',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName={'integrationType'}
              options={[
                { key: 'DIRECT', value: 'DIRECT' },
                { key: 'REVERSE_BGI', value: 'REVERSE_BGI' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.integrationType}</Box>,
        },
        {
          name: 'provider.syncRaindrop',
          title: 'Raindrop Creation Notification',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.syncRaindrop ? 'True' : 'False'}</Box>,
        },
        {
          name: 'provider.syncFreebet',
          title: 'Freebet Creation Notification',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.syncFreebet ? 'True' : 'False'}</Box>,
        },
        {
          name: 'provider.notifyOnLastFreebet',
          title: 'Notify On Last Freebet',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.notifyOnLastFreebet ? 'True' : 'False'}</Box>
          ),
        },
        {
          name: 'provider.enableInactivityScreen',
          title: 'Enable Inactivity Screen',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.enableInactivityScreen ? 'True' : 'False'}</Box>
          ),
        },
        {
          name: 'provider.raindropType',
          title: 'Raindrop Type',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.raindropType}</Box>,
        },
        {
          name: 'provider.createdAt',
          title: 'Created At',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
